// CR087: Client-side OCR using Tesseract.js
// CR088: Added signals_hash for audit trail
// CR176: Added auto-downscaling for oversized pages
// Runs in the browser against rendered page images
// Persists results to DB (evidence_json + S1_OCR step attempt diagnostics_json)

import { useState, useCallback, useRef } from "react";
import { createWorker, Worker } from "tesseract.js";
import { supabase } from "@/integrations/supabase/client";
import { computeCandidateScore, computePrimaryOcrScore } from "@/lib/flyer-pipeline-v2/candidateScoringEngine";
import { prepareImageForOcr, type DownscaleResult } from "@/lib/flyer-pipeline-v2/imageDownscaler";
import type { CandidateScoringConfig, CandidateSignal } from "@/lib/flyer-pipeline-v2/types";
import type { OcrOutcome } from "@/components/admin/batch-runs/flyer-truth-table/types";

// CR088: Simple hash function for signals audit (djb2 algorithm)
async function computeSimpleHash(str: string): Promise<string> {
  // Use Web Crypto API if available for better hashing
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.slice(0, 8).map(b => b.toString(16).padStart(2, '0')).join(''); // First 8 bytes = 16 hex chars
  }
  // Fallback to djb2
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

interface OcrPageResult {
  page_number: number;
  ocr_text: string;
  ocr_text_preview: string;
  ocr_score: number;
  is_candidate: boolean;
  signal_score: number;
  matched_signals: string[];
  ocr_engine_used: "TESSERACT_BROWSER";
  ocr_time_ms: number;
  // CR176: Downscale tracking
  ocr_input_variant: "full" | "ocr_safe";
  ocr_outcome: OcrOutcome;
  render_width: number;
  render_height: number;
  render_megapixels: number;
  ocr_safe_width?: number;
  ocr_safe_height?: number;
  ocr_safe_megapixels?: number;
}

interface StagingItem {
  id: string;
  merchant_key: string;
  evidence_json: Record<string, unknown>;
}

interface OcrProgress {
  status: "IDLE" | "RUNNING" | "COMPLETE" | "FAILED";
  total_pages: number;
  pages_processed: number;
  candidates_found: number;
  current_page?: string;
  errors: string[];
}

interface UseClientSideOcrOptions {
  batchRunId: string;
  config: {
    candidateThreshold: number;
    minOcrScore: number;
    signals: CandidateSignal[];
  };
  onProgress?: (progress: OcrProgress) => void;
  onComplete?: (result: { pages_processed: number; candidates_found: number }) => void;
}

export function useClientSideOcr({ batchRunId, config, onProgress, onComplete }: UseClientSideOcrOptions) {
  const [progress, setProgress] = useState<OcrProgress>({
    status: "IDLE",
    total_pages: 0,
    pages_processed: 0,
    candidates_found: 0,
    errors: [],
  });
  const workerRef = useRef<Worker | null>(null);
  const abortRef = useRef(false);

  const updateProgress = useCallback((update: Partial<OcrProgress>) => {
    setProgress(prev => {
      const newProgress = { ...prev, ...update };
      onProgress?.(newProgress);
      return newProgress;
    });
  }, [onProgress]);

  // Create S1_OCR step attempt in DB with complete config_snapshot
  const createStepAttempt = useCallback(async (totalPages: number): Promise<string | null> => {
    try {
      // Get next attempt number
      const { data: existing } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("attempt_no")
        .eq("batch_run_id", batchRunId)
        .eq("step_key", "S1_OCR")
        .order("attempt_no", { ascending: false })
        .limit(1);

      const nextAttemptNo = (existing?.[0]?.attempt_no ?? 0) + 1;
      const startedAt = new Date().toISOString();
      const enabledSignals = config.signals.filter(s => s.enabled);
      const enabledSignalsCount = enabledSignals.length;
      
      // CR088: Compute hash of signals JSON for audit trail
      const signalsJson = JSON.stringify(enabledSignals.map(s => ({ id: s.id, type: s.type, weight: s.weight })));
      const signalsHash = await computeSimpleHash(signalsJson);

      // CR081/CR088: Complete config_snapshot with all required fields for proof/audit
      const configSnapshot = {
        ocr_engine_selected: "TESSERACT_BROWSER",
        ocr_engine_used: "TESSERACT_BROWSER",
        execution_mode: "CLIENT" as const,
        fallback_used: false,
        fallback_reason: null,
        candidate_threshold: config.candidateThreshold,
        min_ocr_score: config.minOcrScore,
        signals_count: enabledSignalsCount,
        signals_hash: signalsHash, // CR088: Added for audit
        signals_source_path: "batch_run.config_json.flyerExtraction.signals",
        config_source: "batch_run.config_json.flyerExtraction",
      };

      const { data, error } = await supabase
        .from("cr075_batch_run_step_attempts")
        .insert({
          batch_run_id: batchRunId,
          step_key: "S1_OCR",
          attempt_no: nextAttemptNo,
          status: "RUNNING",
          started_at: startedAt,
          diagnostics_json: {
            stage: "OCR_SIGNALS",
            schema_version: "cr081_client_v1",
            batch_run_id: batchRunId,
            status: "RUNNING",
            total_pages: totalPages,
            pages_processed: 0,
            pages_skipped: 0,
            pages_failed: 0,
            candidates_found: 0,
            signal_threshold: config.candidateThreshold,
            top_signals: [],
            config_snapshot: configSnapshot,
            pages: [],
            timing_ms: { total: 0, ocr: 0, signal_scoring: 0 },
            started_at: startedAt,
            ended_at: null,
            last_heartbeat_at: startedAt,
          },
        })
        .select("id")
        .single();

      if (error) {
        console.error("[ClientOCR] Failed to create step attempt:", error);
        return null;
      }
      
      console.log(`[ClientOCR] Created S1_OCR attempt ${nextAttemptNo} with config:`, configSnapshot);
      return data.id;
    } catch (err) {
      console.error("[ClientOCR] Error creating step attempt:", err);
      return null;
    }
  }, [batchRunId, config]);

  // Update step attempt diagnostics
  const updateStepAttempt = useCallback(async (
    attemptId: string,
    update: {
      status?: string;
      pages_processed?: number;
      candidates_found?: number;
      pages?: OcrPageResult[];
      finished_at?: string;
    }
  ) => {
    try {
      // Get current diagnostics
      const { data: current } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("diagnostics_json")
        .eq("id", attemptId)
        .single();

      const currentDiag = (current?.diagnostics_json as Record<string, unknown>) || {};
      const updatedDiag = {
        ...currentDiag,
        ...update,
        last_heartbeat_at: new Date().toISOString(),
      };

      const updatePayload: Record<string, unknown> = {
        diagnostics_json: updatedDiag,
      };
      if (update.status && (update.status === "COMPLETE" || update.status === "FAILED" || update.status === "PARTIAL")) {
        updatePayload.status = update.status;
        updatePayload.finished_at = update.finished_at || new Date().toISOString();
      }

      await supabase
        .from("cr075_batch_run_step_attempts")
        .update(updatePayload)
        .eq("id", attemptId);
    } catch (err) {
      console.error("[ClientOCR] Failed to update step attempt:", err);
    }
  }, []);

  // Get signed URL for a storage path
  const getSignedUrl = useCallback(async (storagePath: string): Promise<string | null> => {
    try {
      // Normalize the path - strip any URL prefixes
      let cleanPath = storagePath;
      if (cleanPath.includes("/object/")) {
        cleanPath = cleanPath.split("/object/")[1];
        if (cleanPath.startsWith("sign/") || cleanPath.startsWith("public/")) {
          cleanPath = cleanPath.replace(/^(sign|public)\//, "");
        }
      }
      // Remove bucket prefix if present
      if (cleanPath.startsWith("flyer-fetch-assets/")) {
        cleanPath = cleanPath.replace("flyer-fetch-assets/", "");
      }

      const { data, error } = await supabase.storage
        .from("flyer-fetch-assets")
        .createSignedUrl(cleanPath, 900); // 15 min TTL

      if (error) {
        console.error("[ClientOCR] Failed to get signed URL:", error);
        return null;
      }
      return data.signedUrl;
    } catch (err) {
      console.error("[ClientOCR] Error getting signed URL:", err);
      return null;
    }
  }, []);

  // CR176: Run OCR on a single page with auto-downscaling for oversized pages
  const ocrPage = useCallback(async (
    worker: Worker,
    imageUrl: string,
    pageNumber: number,
    flyerMerchant: string
  ): Promise<OcrPageResult | null> => {
    const startTime = Date.now();
    try {
      // CR176: Prepare image - downscale if oversized
      console.log(`[ClientOCR] Preparing image for page ${pageNumber}...`);
      const prepResult = await prepareImageForOcr(imageUrl);
      
      // Check for downscale error
      if (prepResult.error) {
        console.error(`[ClientOCR] Image preparation failed for page ${pageNumber}:`, prepResult.error);
        // Return a result with DOWNSCALE_FAILED outcome
        return {
          page_number: pageNumber,
          ocr_text: "",
          ocr_text_preview: "",
          ocr_score: 0,
          is_candidate: false,
          signal_score: 0,
          matched_signals: [],
          ocr_engine_used: "TESSERACT_BROWSER",
          ocr_time_ms: Date.now() - startTime,
          ocr_input_variant: prepResult.wasDownscaled ? "ocr_safe" : "full",
          ocr_outcome: "OCR_DOWNSCALED_FAILED",
          render_width: prepResult.originalWidth,
          render_height: prepResult.originalHeight,
          render_megapixels: prepResult.originalMegapixels,
          ocr_safe_width: prepResult.newWidth || undefined,
          ocr_safe_height: prepResult.newHeight || undefined,
          ocr_safe_megapixels: prepResult.newMegapixels || undefined,
        };
      }
      
      // Run OCR on the (possibly downscaled) image
      console.log(`[ClientOCR] Running Tesseract OCR on page ${pageNumber} (${prepResult.wasDownscaled ? 'downscaled' : 'full'})...`);
      const result = await worker.recognize(prepResult.imageUrl);
      const ocrText = result.data.text || "";
      const ocrTimeMs = Date.now() - startTime;

      // Compute OCR quality score
      const ocrScoreResult = computePrimaryOcrScore(ocrText);

      // Compute candidate score using signals
      const scoringConfig: CandidateScoringConfig = {
        version: "v1",
        enabled: true,
        threshold: config.candidateThreshold,
        min_primary_ocr_score: config.minOcrScore,
        signals: config.signals,
      };
      const candidateResult = computeCandidateScore(ocrText, ocrScoreResult.total, scoringConfig);

      // Get matched signal names
      const matchedSignals = candidateResult.score_breakdown
        .filter(s => s.fired && s.delta > 0)
        .map(s => s.signal_name);
      
      // CR176: Determine OCR outcome based on downscale status and text
      let ocrOutcome: OcrOutcome;
      if (prepResult.wasDownscaled) {
        ocrOutcome = ocrText.length > 0 ? "OCR_DOWNSCALED_OK" : "OCR_DOWNSCALED_EMPTY";
      } else {
        ocrOutcome = ocrText.length > 0 ? "OCR_OK" : "OCR_EMPTY";
      }

      return {
        page_number: pageNumber,
        ocr_text: ocrText,
        ocr_text_preview: ocrText.substring(0, 500),
        ocr_score: ocrScoreResult.total,
        is_candidate: candidateResult.candidate_for_ai,
        signal_score: candidateResult.candidate_score,
        matched_signals: matchedSignals,
        ocr_engine_used: "TESSERACT_BROWSER",
        ocr_time_ms: ocrTimeMs,
        // CR176: Downscale tracking
        ocr_input_variant: prepResult.wasDownscaled ? "ocr_safe" : "full",
        ocr_outcome: ocrOutcome,
        render_width: prepResult.originalWidth,
        render_height: prepResult.originalHeight,
        render_megapixels: prepResult.originalMegapixels,
        ocr_safe_width: prepResult.wasDownscaled ? prepResult.newWidth : undefined,
        ocr_safe_height: prepResult.wasDownscaled ? prepResult.newHeight : undefined,
        ocr_safe_megapixels: prepResult.wasDownscaled ? prepResult.newMegapixels : undefined,
      };
    } catch (err) {
      console.error(`[ClientOCR] OCR failed for page ${pageNumber}:`, err);
      return null;
    }
  }, [config]);

  // Update staging item with OCR results (CR176: includes downscale metadata)
  const updateStagingItem = useCallback(async (
    itemId: string,
    ocrPages: OcrPageResult[],
    candidatePages: OcrPageResult[]
  ) => {
    try {
      // Get current evidence
      const { data: item } = await supabase
        .from("cr075_staging_deal_items")
        .select("evidence_json")
        .eq("id", itemId)
        .single();

      const evidence = (item?.evidence_json as Record<string, unknown>) || {};
      
      // CR176: Track downscaled pages summary
      const downscaledCount = ocrPages.filter(p => p.ocr_input_variant === "ocr_safe").length;
      
      // Update with OCR results - CR176: include all downscale tracking fields
      const updatedEvidence = {
        ...evidence,
        ocr_scan_status: "COMPLETE",
        ocr_engine_used: "TESSERACT_BROWSER",
        ocr_downscaled_pages_count: downscaledCount, // CR176
        ocr_pages: ocrPages.map(p => ({
          page_number: p.page_number,
          ocr_text_preview: p.ocr_text_preview,
          ocr_score: p.ocr_score,
          signal_score: p.signal_score,
          is_candidate: p.is_candidate,
          matched_signals: p.matched_signals,
          ocr_engine_used: p.ocr_engine_used,
          ocr_time_ms: p.ocr_time_ms,
          // CR176: Downscale tracking
          ocr_input_variant: p.ocr_input_variant,
          ocr_outcome: p.ocr_outcome,
          render_width: p.render_width,
          render_height: p.render_height,
          render_megapixels: p.render_megapixels,
          ocr_safe_width: p.ocr_safe_width,
          ocr_safe_height: p.ocr_safe_height,
          ocr_safe_megapixels: p.ocr_safe_megapixels,
        })),
        candidate_pages: candidatePages.map(p => ({
          page_number: p.page_number,
          score: p.signal_score,
          matched_signals: p.matched_signals,
          reason: `Score ${p.signal_score} >= threshold ${config.candidateThreshold}`,
        })),
      };

      await supabase
        .from("cr075_staging_deal_items")
        .update({ evidence_json: updatedEvidence })
        .eq("id", itemId);
    } catch (err) {
      console.error("[ClientOCR] Failed to update staging item:", err);
    }
  }, [config.candidateThreshold]);

  // Main run function
  const runOcr = useCallback(async () => {
    abortRef.current = false;
    updateProgress({ status: "RUNNING", pages_processed: 0, candidates_found: 0, errors: [] });

    try {
      // Fetch all flyer staging items
      const { data: stagingItems, error } = await supabase
        .from("cr075_staging_deal_items")
        .select("id, merchant_key, evidence_json")
        .eq("batch_run_id", batchRunId)
        .eq("source_type", "FLYER");

      if (error) throw error;
      if (!stagingItems || stagingItems.length === 0) {
        updateProgress({ status: "COMPLETE", total_pages: 0 });
        return;
      }

      // Count total pages
      let totalPages = 0;
      const itemsWithPages: Array<{ item: StagingItem; pages: Array<{ page_number: number; storage_path: string }> }> = [];

      for (const item of stagingItems) {
        const evidence = item.evidence_json as Record<string, unknown>;
        const flyerPages = Array.isArray(evidence?.flyer_pages) ? evidence.flyer_pages as Array<{ page_number: number; storage_path: string }> : [];
        if (flyerPages.length > 0 && evidence?.render_status === "RENDERED") {
          totalPages += flyerPages.length;
          itemsWithPages.push({ item: item as StagingItem, pages: flyerPages });
        }
      }

      if (totalPages === 0) {
        updateProgress({ status: "COMPLETE", total_pages: 0 });
        return;
      }

      updateProgress({ total_pages: totalPages });

      // Create step attempt
      const attemptId = await createStepAttempt(totalPages);
      if (!attemptId) {
        throw new Error("Failed to create step attempt");
      }

      // Initialize Tesseract worker
      console.log("[ClientOCR] Initializing Tesseract worker...");
      const worker = await createWorker("eng");
      workerRef.current = worker;

      let pagesProcessed = 0;
      let candidatesFound = 0;
      const allPageResults: OcrPageResult[] = [];
      const errors: string[] = [];

      // Process each flyer
      for (const { item, pages } of itemsWithPages) {
        if (abortRef.current) break;

        const flyerOcrResults: OcrPageResult[] = [];
        const flyerCandidates: OcrPageResult[] = [];

        for (const page of pages) {
          if (abortRef.current) break;

          updateProgress({ current_page: `${item.merchant_key} p${page.page_number}` });

          // Get signed URL for the page image
          const signedUrl = await getSignedUrl(page.storage_path);
          if (!signedUrl) {
            errors.push(`Failed to get URL for ${item.merchant_key} page ${page.page_number}`);
            continue;
          }

          // Run OCR
          const result = await ocrPage(worker, signedUrl, page.page_number, item.merchant_key);
          if (result) {
            flyerOcrResults.push(result);
            allPageResults.push(result);

            if (result.is_candidate) {
              flyerCandidates.push(result);
              candidatesFound++;
            }
          } else {
            errors.push(`OCR failed for ${item.merchant_key} page ${page.page_number}`);
          }

          pagesProcessed++;
          updateProgress({ pages_processed: pagesProcessed, candidates_found: candidatesFound, errors });

          // Update step attempt every few pages (CR176: include downscale metadata)
          if (pagesProcessed % 3 === 0 || pagesProcessed === totalPages) {
            await updateStepAttempt(attemptId, {
              status: "RUNNING",
              pages_processed: pagesProcessed,
              candidates_found: candidatesFound,
              pages: allPageResults.map(p => ({
                flyer_merchant: item.merchant_key,
                page_number: p.page_number,
                ocr_text_preview: p.ocr_text_preview,
                ocr_score: p.ocr_score,
                is_candidate: p.is_candidate,
                signal_score: p.signal_score,
                triggered_signals: p.matched_signals,
                // CR176: Downscale tracking in diagnostics
                ocr_input_variant: p.ocr_input_variant,
                ocr_outcome: p.ocr_outcome,
                render_width: p.render_width,
                render_height: p.render_height,
                render_megapixels: p.render_megapixels,
                ocr_safe_width: p.ocr_safe_width,
                ocr_safe_height: p.ocr_safe_height,
                ocr_safe_megapixels: p.ocr_safe_megapixels,
              })) as unknown as OcrPageResult[],
            });
          }
        }

        // Update staging item with OCR results
        await updateStagingItem(item.id, flyerOcrResults, flyerCandidates);
      }

      // Terminate worker
      await worker.terminate();
      workerRef.current = null;

      // Mark complete
      const finalStatus = errors.length > 0 ? "PARTIAL" : "COMPLETE";
      await updateStepAttempt(attemptId, {
        status: finalStatus,
        pages_processed: pagesProcessed,
        candidates_found: candidatesFound,
        finished_at: new Date().toISOString(),
      });

      updateProgress({ status: finalStatus as OcrProgress["status"], current_page: undefined });
      onComplete?.({ pages_processed: pagesProcessed, candidates_found: candidatesFound });

    } catch (err) {
      console.error("[ClientOCR] OCR run failed:", err);
      updateProgress({ 
        status: "FAILED", 
        errors: [...progress.errors, err instanceof Error ? err.message : "Unknown error"] 
      });
    }
  }, [batchRunId, config, updateProgress, createStepAttempt, getSignedUrl, ocrPage, updateStagingItem, updateStepAttempt, onComplete, progress.errors]);

  // Abort function
  const abort = useCallback(() => {
    abortRef.current = true;
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    updateProgress({ status: "FAILED", current_page: undefined });
  }, [updateProgress]);

  return {
    progress,
    runOcr,
    abort,
    isRunning: progress.status === "RUNNING",
  };
}
